import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import moment from "moment";

import { GET_ALL_USER_KPIS } from "../../../api/kpi";
import { buildYetiScoresForKpis, UseUsersDictionary, windowUsesRangeYeti } from "../../../hooks/company";
import { companyMonthStr, companyToday } from "../../../helpers/companyTime";
import {
  foldDayKpis, metaByIdFromDefs, sortDaysAsc, backfillMonthPinnedRates, isRateLikeKpi, isCompositeMeta,
} from "../../../helpers/scoreAggregate";
import { hasMetricPlan, sourceWindowOf } from "../../../helpers/metricPlan";
import { applyCompositeOperand } from "../../../constants/kpi";
import { ptsPerAmtOf } from "../../../helpers/kpiPoints";
import {
  needsWindowRates,
  boundsForWindow,
  fetchWindowRates,
  mergeWindowRatesIntoBucket,
} from "../../../helpers/windowRates";

/**
 * Workspace-scoped data for dashboard COMPONENTS (Beat the Yeti, Leaderboard).
 *
 * Scores are stored per calendar month on the server. Today / week / month only
 * need the current month; quarter and YTD stitch several months together.
 */

const scoreCache = new Map(); // cacheKey → { at, scores }
const inflightScores = new Map(); // cacheKey → Promise
// Short TTL — this cache exists only so several cards mounting at once (or a
// week↔month filter flip) don't refire the same fetch, not to keep serving
// data for minutes after it goes stale. Nothing invalidates it on its own
// when a metric gets added or assigned onto a workspace (see
// clearWorkspaceScoresCache below for the explicit case), so a long TTL here
// meant a newly-assigned metric could read $0/stale for up to 5 minutes.
const CACHE_MS = 30 * 1000;

/**
 * Drop cached scores so the next read refetches from the server. Call this
 * whenever a workspace's metric set changes (assigned/unassigned, a new
 * integration metric added) — the cache key is `${workspaceId}|${months}`
 * and has no idea a metric was added, so it would otherwise keep serving the
 * pre-change payload until CACHE_MS elapses.
 *
 * @param {string} [workspaceId] - clear only this workspace's entries; omit to clear everything.
 */
export const clearWorkspaceScoresCache = (workspaceId) => {
  if (!workspaceId) {
    scoreCache.clear();
    return;
  }
  const prefix = `${workspaceId}|`;
  [...scoreCache.keys()].filter((key) => key.startsWith(prefix)).forEach((key) => scoreCache.delete(key));
};

const PERIOD_START = {
  today: () => companyToday().startOf("day"),
  yesterday: () => companyToday().subtract(1, "day").startOf("day"),
  // Sunday-start weeks — same as Homepage / kpi saga buckets.
  week: () => companyToday().startOf("week"),
  lastWeek: () => companyToday().subtract(1, "week").startOf("week"),
  lastMonth: () => companyToday().subtract(1, "month").startOf("month"),
  month: () => companyToday().startOf("month"),
  quarter: () => companyToday().startOf("quarter"),
  ytd: () => companyToday().startOf("year"),
  year: () => companyToday().startOf("year"),
};

/** Every YYYY-MM from start through end inclusive (company calendar). */
const monthsBetween = (start, end) => {
  const out = [];
  const cursor = moment(start).startOf("month");
  const last = moment(end).startOf("month");
  if (!cursor.isValid() || !last.isValid()) return [companyToday().format("YYYY-MM")];
  while (cursor.isSameOrBefore(last, "month")) {
    out.push(cursor.format("YYYY-MM"));
    cursor.add(1, "month");
  }
  return out.length ? out : [companyToday().format("YYYY-MM")];
};

/**
 * YYYY-MM strings needed to cover a window (server is one month per call).
 * Pass `custom: { from, to }` when win is "custom" (or any explicit range).
 */
export const monthsForWindow = (win, custom = null) => {
  const now = companyToday();
  if (win === "custom" || (custom && (custom.from || custom.to))) {
    const from = custom?.from ? moment(custom.from).startOf("day") : now.clone().startOf("month");
    const to = custom?.to ? moment(custom.to).endOf("day") : now.clone().endOf("day");
    return monthsBetween(from, to);
  }
  if (win === "ytd" || win === "year") {
    return monthsBetween(now.clone().startOf("year"), now);
  }
  if (win === "quarter") {
    return monthsBetween(now.clone().startOf("quarter"), now);
  }
  if (win === "week") {
    return monthsBetween(now.clone().startOf("week"), now.clone().endOf("week"));
  }
  if (win === "lastWeek") {
    const start = now.clone().subtract(1, "week").startOf("week");
    const end = start.clone().endOf("week");
    return monthsBetween(start, end);
  }
  if (win === "lastMonth") {
    const start = now.clone().subtract(1, "month").startOf("month");
    return [start.format("YYYY-MM")];
  }
  if (win === "yesterday") {
    const y = now.clone().subtract(1, "day");
    return [y.format("YYYY-MM")];
  }
  return [now.format("YYYY-MM")];
};

/** Union of YYYY-MM months needed for a metric plan + board window. */
export const monthsForMetricPlan = (plan, boardWin = "month", boardCustom = null) => {
  const norm = boardWin === "year" ? "ytd" : boardWin;
  const months = new Set(monthsForWindow(norm, boardCustom));
  if (!hasMetricPlan(plan)) return [...months];
  (plan.cols || []).forEach((col) => {
    Object.entries(col.sources || {}).forEach(([wsId, kpiId]) => {
      if (!kpiId) return;
      const sw = sourceWindowOf(col, wsId, norm, boardCustom);
      const win = sw.window === "year" ? "ytd" : sw.window;
      monthsForWindow(win, sw.custom).forEach((m) => months.add(m));
    });
  });
  return [...months];
};

const mergeScoreLists = (lists) => {
  const byOwner = new Map();
  (lists || []).forEach((scores) => {
    (scores || []).forEach((s) => {
      const id = String(s.ownerId || s.userId || "");
      if (!id) return;
      const prev = byOwner.get(id);
      if (!prev) {
        byOwner.set(id, { ...s, days: [...(s.days || [])] });
      } else {
        prev.days = [...(prev.days || []), ...(s.days || [])];
      }
    });
  });
  return [...byOwner.values()];
};

/** Fetch (with cache + in-flight coalesce) one workspace's scores — one or more months. */
export const fetchWorkspaceScores = async (companyId, workspaceId, months = null) => {
  const list = months && months.length ? months : [companyToday().format("YYYY-MM")];
  const cacheKey = `${workspaceId}|${list.join(",")}`;
  const cached = scoreCache.get(cacheKey);
  /* Always return a Promise (this fn is async). Cache hits resolve immediately
     so week↔month filter changes don't wait on the network. */
  if (cached && Date.now() - cached.at < CACHE_MS) return cached.scores;

  /* Year filter mounts several cards at once — without coalesce they each fire
     the same 8-month fetch and the board crawls while "Saving…" also runs. */
  const pending = inflightScores.get(cacheKey);
  if (pending) return pending;

  const req = Promise.all(
    list.map((month) =>
      GET_ALL_USER_KPIS(companyId, workspaceId, month)
        .then((res) => res?.data?.scores || res?.scores || [])
        .catch(() => []),
    ),
  )
    .then((parts) => {
      const scores = mergeScoreLists(parts);
      /* Never cache an empty payload — a cold-start race (auth / KPI catalog)
         used to stick [] for five minutes and every scoped card read $0. */
      if (scores.length) scoreCache.set(cacheKey, { at: Date.now(), scores });
      return scores;
    })
    .finally(() => {
      inflightScores.delete(cacheKey);
    });

  inflightScores.set(cacheKey, req);
  return req;
};

export const periodStart = (period) => (PERIOD_START[period] || PERIOD_START.month)();

/** The dates a window means. Presets fill in from → to (Sunday-start weeks). */
export const resolveWindow = (win, custom) => {
  const today = companyToday();
  if (win === "custom") {
    const from = custom?.from ? moment(custom.from).startOf("day") : today.clone().startOf("month");
    let to = custom?.to ? moment(custom.to).endOf("day") : today.clone().endOf("day");
    // Never include days after today — scores don't exist there.
    const endToday = today.clone().endOf("day");
    if (to.isAfter(endToday)) to = endToday.clone();
    if (from.isAfter(endToday)) {
      // Entire range is in the future → empty window (no scores).
      return { from: endToday.clone().add(1, "ms"), to: endToday.clone() };
    }
    return { from, to };
  }
  if (win === "today") return { from: today.clone().startOf("day"), to: today.clone().endOf("day") };
  if (win === "yesterday") {
    const y = today.clone().subtract(1, "day");
    return { from: y.clone().startOf("day"), to: y.clone().endOf("day") };
  }
  if (win === "week") {
    return { from: today.clone().startOf("week"), to: today.clone().endOf("day") };
  }
  if (win === "lastWeek") {
    const start = today.clone().subtract(1, "week").startOf("week");
    return { from: start, to: start.clone().endOf("week") };
  }
  if (win === "lastMonth") {
    const start = today.clone().subtract(1, "month").startOf("month");
    return { from: start, to: start.clone().endOf("month") };
  }
  if (win === "quarter") return { from: today.clone().startOf("quarter"), to: today.clone().endOf("day") };
  if (win === "ytd" || win === "year") {
    return { from: today.clone().startOf("year"), to: today.clone().endOf("day") };
  }
  return { from: today.clone().startOf("month"), to: today.clone().endOf("day") };
};

/**
 * backfillMonthPinnedRates treats a composite (isCompositeMeta) like any
 * other month-pinned rate — copy whichever month-snapshot day-row it can
 * find, verbatim. That beats a hard 0, but it's still the wrong number for
 * anything shorter than the whole month: "Yesterday" ends up showing
 * September's full-month rate, not yesterday's. Closed Rate / Bid Accuracy /
 * AVG Ticket fix that with a real server-side recompute per window
 * (isWindowRateKpi, /kpi/window-rates) — a composite has no such endpoint,
 * but its own two source metrics (Revenue Credit, Hours Worked, …) are plain
 * additive $ KPIs that already folded correctly for the exact requested
 * window above, so a real window-scoped rate is one divide away. Recompute
 * it and let that win over the copied month snapshot, for any window that
 * isn't an exact single calendar month (the one case the server's own
 * monthly snapshot is already the right, more precise answer — see
 * gmb-server compositeRecompute.js).
 */
const isFullCalendarMonth = (from, to) => {
  const f = moment(from);
  const t = moment(to);
  if (!f.isValid() || !t.isValid()) return false;
  if (f.year() !== t.year() || f.month() !== t.month()) return false;
  return f.isSame(f.clone().startOf("month"), "day") && t.isSame(f.clone().endOf("month"), "day");
};

const applyCompositeWindowRecompute = (bucket, metaById, trustFoldedValue) => {
  if (!metaById) return;
  Object.keys(metaById).forEach((id) => {
    const meta = metaById[id];
    if (!isCompositeMeta(meta)) return;
    if (trustFoldedValue) return;
    const src1 = bucket[String(meta.optional.sourceId1)];
    const src2 = bucket[String(meta.optional.sourceId2)];
    if (!src1 && !src2) return; // nothing to derive from — leave whatever the fold/backfill produced
    const amt = applyCompositeOperand(Number(src1?.amt) || 0, Number(src2?.amt) || 0, meta.optional.operand);
    const hasFullConfig = Number(meta.pointValue) > 0 && Number(meta.pointScale) > 0;
    const pts = meta.metricType !== "anchor" && hasFullConfig
      ? amt * ptsPerAmtOf(meta)
      : Number(bucket[id]?.pts) || 0;
    bucket[id] = { amt, pts, scored: true };
  });
};

/**
 * Fold per-day scores from `from` through optional `to` into per-kpi / per-user totals.
 * Rate KPIs (Closed Rate, …) keep the latest day in the window — summing
 * 0–1 snapshots was printing YTD as "1%" / "2%". Pass `metaById` from defs.
 */
export const sumScores = (scores, from, metaById = null, to = null) => {
  const perKpi = {};
  const perUser = {};
  const trustFoldedComposite = isFullCalendarMonth(from, to);
  (scores || []).forEach((score) => {
    const ownerId = score.ownerId || score.userId || null;
    const allDays = sortDaysAsc(score.days);
    const days = allDays.filter((day) => {
      const d = moment(day.day);
      if (!d.isValid() || d.isBefore(from, "day")) return false;
      if (to && d.isAfter(to, "day")) return false;
      return true;
    });
    const userKpis = {};
    days.forEach((day) => {
      foldDayKpis(userKpis, day.kpis, metaById);
      foldDayKpis(perKpi, day.kpis, metaById);
    });
    /* Rates pinned on YYYY-MM-01 are outside Today/Week after the 1st. */
    if (to) {
      backfillMonthPinnedRates(userKpis, allDays, from, to, metaById);
      Object.entries(userKpis).forEach(([id, cell]) => {
        if ((Number(cell?.amt) || 0) <= 0) return;
        if ((Number(perKpi[id]?.amt) || 0) > 0) return;
        if (!isRateLikeKpi(metaById?.[id])) return;
        perKpi[id] = {
          amt: Number(cell.amt) || 0,
          pts: Number(cell.pts) || 0,
          scored: true,
        };
      });
    }
    applyCompositeWindowRecompute(userKpis, metaById, trustFoldedComposite);
    if (!ownerId) return;
    const total = Object.values(userKpis).reduce((s, c) => s + (Number(c.pts) || 0), 0);
    perUser[ownerId] = { total, kpis: userKpis };
  });
  applyCompositeWindowRecompute(perKpi, metaById, trustFoldedComposite);
  return { perKpi, perUser };
};

export default function useWorkspaceData(workspaceId, { period = "month", custom = null } = {}) {
  const companyId = useSelector((state) => state.authReducer.userDetails?.companyId);
  const companyKpis = useSelector((state) => state.kpiReducer.companyKpis);
  const holidays = useSelector((state) => state.companyReducer.holidays);
  const usersDict = UseUsersDictionary();

  const win = period === "year" ? "ytd" : period;
  const customKey = custom ? `${custom.from || ""}~${custom.to || ""}` : "";
  const [scores, setScores] = useState(null);
  const [loading, setLoading] = useState(!!workspaceId);
  const [windowRates, setWindowRates] = useState(null);

  useEffect(() => {
    if (!companyId || !workspaceId) return undefined;
    let alive = true;
    const months = monthsForWindow(win, custom);
    setLoading(true);
    fetchWorkspaceScores(companyId, workspaceId, months)
      .then((s) => {
        if (!alive) return;
        setScores(s);
        setLoading(false);
      })
      .catch(() => {
        if (!alive) return;
        setScores([]);
        setLoading(false);
      });
    return () => { alive = false; };
  }, [companyId, workspaceId, win, customKey]);

  useEffect(() => {
    if (!companyId || !workspaceId || !needsWindowRates(win)) {
      setWindowRates(null);
      return undefined;
    }
    let alive = true;
    const { from, to } = boundsForWindow(win, custom);
    fetchWindowRates({ groupId: workspaceId, from, to }).then((rates) => {
      if (alive) setWindowRates(rates);
    });
    return () => { alive = false; };
  }, [companyId, workspaceId, win, customKey]);

  return useMemo(() => {
    const defs = (companyKpis && companyKpis[workspaceId]) || [];
    const { from, to } = resolveWindow(win, custom);
    const metaById = metaByIdFromDefs(defs);
    const { perKpi, perUser } = sumScores(scores, from, metaById, to);

    if (windowRates) {
      Object.entries(windowRates).forEach(([ownerId, rates]) => {
        if (!perUser[ownerId]) {
          perUser[ownerId] = { total: 0, kpis: {} };
        }
        mergeWindowRatesIntoBucket(perUser[ownerId].kpis, rates);
        perUser[ownerId].total = Object.values(perUser[ownerId].kpis)
          .reduce((s, c) => s + (Number(c?.pts) || 0), 0);
      });
      Object.values(windowRates).forEach((rates) => {
        Object.entries(rates || {}).forEach(([kpiId, cell]) => {
          if (!cell) return;
          /* Rebuild team-level cell from player overlays — average of stamped amts. */
          let sum = 0;
          let pts = 0;
          let n = 0;
          Object.values(perUser).forEach((u) => {
            const c = u.kpis?.[kpiId];
            if (!c?.windowRate) return;
            sum += Number(c.amt) || 0;
            pts += Number(c.pts) || 0;
            n += 1;
          });
          if (n) {
            perKpi[kpiId] = {
              amt: sum / n,
              pts: pts / n,
              scored: true,
            };
          }
        });
      });
    }

    const kpis = defs.map((k) => ({
      ...k,
      amount: perKpi[k._id]?.amt || 0,
      points: perKpi[k._id]?.pts || 0,
    }));

    const yetiMonthRef = windowUsesRangeYeti(win) && from?.isValid?.()
      ? from.format("YYYY-MM")
      : companyMonthStr();
    const yetiAsOf = windowUsesRangeYeti(win) && to?.isValid?.()
      ? moment.min(to.clone().endOf("day"), companyToday().endOf("day"))
      : companyToday();
    const yetiRange = windowUsesRangeYeti(win) && from?.isValid?.()
      ? {
        from: from.format("YYYY-MM-DD"),
        to: (to?.isValid?.() ? to : from).format("YYYY-MM-DD"),
      }
      : null;
    const rawYeti = defs.length
      ? buildYetiScoresForKpis(defs, yetiMonthRef, holidays, yetiAsOf, yetiRange)
      : null;
    const yeti = rawYeti && windowUsesRangeYeti(win) && rawYeti.custom
      ? { ...rawYeti, month: rawYeti.custom }
      : rawYeti;

    const players = Object.entries(perUser)
      .map(([userId, v]) => ({
        userId,
        pts: Math.round(v.total * 100) / 100,
        kpis: v.kpis,
        ...(usersDict?.[userId] || {}),
      }))
      .filter((p) => p.isActive !== false && p.lastName !== "Yeti")
      .sort((a, b) => b.pts - a.pts);

    const teamPts = players.reduce((sum, p) => sum + p.pts, 0);

    return { kpis, kpisAll: kpis, yeti, players, teamPts, loading };
  }, [companyKpis, workspaceId, scores, windowRates, win, customKey, holidays, usersDict, loading]);
}
